import { useState } from "react";
import { initialValues } from "@/content/engage";
import { Mono } from "@/components/site/primitives";
import { cn } from "@/lib/utils";

const LABEL = "mb-2 block font-mono text-[10.5px] uppercase tracking-[0.18em] text-o-dim";
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Choices = ({ f, value, onChange }) => {
  const multi = f.type === "multi";
  const selected = multi ? value || [] : value;
  const toggle = (o) => {
    if (multi) {
      onChange(selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o]);
    } else {
      onChange(selected === o ? undefined : o);
    }
  };
  return (
    <div role="group" aria-labelledby={`label-${f.id}`} className="flex flex-wrap gap-2">
      {f.options.map((o) => {
        const on = multi ? selected.includes(o) : selected === o;
        return (
          <button key={o} type="button" aria-pressed={on} onClick={() => toggle(o)} data-testid={`choice-${f.id}-${slug(o)}`}
            className={cn("border px-3 py-2 text-sm transition-colors", on ? "border-o-accent bg-o-accent text-o-accent-ink" : "border-o-strong hover:border-o-accent")}>
            {o}
          </button>
        );
      })}
    </div>
  );
};

const Field = ({ f, value, onChange }) => {
  const id = `field-${f.id}`;
  const wide = f.wide || !["text", "email"].includes(f.type);
  const common = { id, value: value || "", onChange: (e) => onChange(e.target.value), placeholder: f.placeholder, "data-testid": `input-${f.id}`, className: "field-input" };
  return (
    <div className={cn(wide && "sm:col-span-2")}>
      {f.type === "check" ? (
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`input-${f.id}`} className="mt-0.5 h-4 w-4 accent-[var(--o-accent)]" />
          <span>{f.label}</span>
        </label>
      ) : (
        <>
          {["choice", "multi"].includes(f.type) ? (
            <span id={`label-${f.id}`} className={LABEL}>{f.label}{f.type === "multi" && " · choose any"}</span>
          ) : (
            <label htmlFor={id} className={LABEL}>{f.label}</label>
          )}
          {f.type === "textarea" && <textarea rows={5} {...common} />}
          {["text", "email"].includes(f.type) && <input type={f.type} {...common} />}
          {["choice", "multi"].includes(f.type) && <Choices f={f} value={value} onChange={onChange} />}
        </>
      )}
    </div>
  );
};

export const InquiryForm = ({ path, from, originLabel }) => {
  const [values, setValues] = useState(() => initialValues(path.id, from));
  const [previewed, setPreviewed] = useState(false);
  const set = (id, v) => setValues((s) => ({ ...s, [id]: v }));
  return (
    <form data-testid={`inquiry-form-${path.id}`} noValidate autoComplete="off"
      onSubmit={(e) => { e.preventDefault(); setPreviewed(true); }}
      className="mt-8 border border-o-strong bg-o-surface">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-dashed border-o-proof px-5 py-3 sm:px-7">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-o-proof" data-testid="inquiry-inactive-label">
          Inactive preview · nothing is sent or stored
        </span>
        {originLabel && <span data-testid="inquiry-context"><Mono>Context · {originLabel}</Mono></span>}
      </div>
      <div className="grid gap-6 p-5 sm:grid-cols-2 sm:p-7">
        {path.fields.map((f) => <Field key={f.id} f={f} value={values[f.id]} onChange={(v) => set(f.id, v)} />)}
      </div>
      <div className="flex flex-col gap-4 border-t border-o-line px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
        <p className="max-w-md text-xs text-o-dim">Final inquiry wording, routing, and privacy terms will be established in a later phase.</p>
        <button type="submit" data-testid="inquiry-preview-submit"
          className="border border-o-strong px-5 py-3 text-sm font-medium transition-colors hover:border-o-accent hover:bg-o-soft">
          Preview submission
        </button>
      </div>
      {previewed && (
        <div role="status" data-testid="inquiry-preview-notice" className="border-t border-dashed border-o-proof px-5 py-4 text-sm sm:px-7">
          <span className="font-semibold text-o-proof">Preview only.</span> This form is not connected to any destination. Nothing was sent, stored, or transmitted.
        </div>
      )}
    </form>
  );
};
